'use client'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { X } from 'lucide-react'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { MediaImage as Image } from '@/components/media-image'

interface ReviewLightboxProps {
  photos: { src: string; alt: string }[]
  initialIndex?: number
  onClose: () => void
}

/**
 * Fullscreen photo viewer built on base-ui Dialog primitives + the shadcn
 * Carousel (embla). Dialog manages focus, escape and body scroll lock;
 * Carousel manages drag, swipe, arrow keys and prev/next. We use the
 * primitives directly so the panel can own its fullscreen backdrop and
 * dark fill without fighting the default popover styling.
 */
export function ReviewLightbox({ photos, initialIndex = 0, onClose }: ReviewLightboxProps) {
  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 fixed inset-0 z-50 bg-black" />
        <DialogPrimitive.Popup className="data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 fixed inset-0 z-50 size-svh w-screen text-white outline-none">
          <DialogPrimitive.Title className="sr-only">Photo viewer</DialogPrimitive.Title>

          <Carousel
            opts={{ startIndex: initialIndex, loop: photos.length > 1 }}
            className="size-full"
          >
            <CarouselContent className="-ml-0 h-svh">
              {photos.map((photo, i) => (
                <CarouselItem
                  key={photo.src}
                  className="flex h-svh items-center justify-center pl-0"
                >
                  <div className="relative size-full p-6 sm:p-12 lg:p-16">
                    <Image
                      src={photo.src}
                      alt={photo.alt}
                      fill
                      sizes="(min-width: 1280px) 1200px, 90vw"
                      className="object-contain"
                      priority={i === initialIndex}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {photos.length > 1 && (
              <>
                <CarouselPrevious className="left-3 size-10 border-white/20 bg-black/60 text-white hover:bg-black/80 hover:text-white sm:left-6" />
                <CarouselNext className="right-3 size-10 border-white/20 bg-black/60 text-white hover:bg-black/80 hover:text-white sm:right-6" />
              </>
            )}
          </Carousel>

          <DialogPrimitive.Close
            className="absolute top-4 right-4 z-10 inline-flex size-10 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80 sm:top-6 sm:right-6"
            aria-label="Close photo viewer"
          >
            <X className="size-5" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
